import { QuantityDialog } from '@ordr-as/umami-react/dialog';
import { FC, useState } from 'react';
import { MenuItemVariantDto } from '../../../../../../../generated-api/models/MenuItemVariantDto';
import { sumCartAddition } from '../../../../../../../types/calculation';
import { AddItemsDto, CartAdditionOption, DeleteItemsDto, EditItemWrapper } from '../../../../../../../types/cart';
import { useLocation } from '../../../../../../context/LocationContext';
import { useScreenSize } from '../../../../../../context/ScreenSizeContext';
import { useCustomisations } from '../../../../../../hooks/useCustomisations';
import useIntl from '../../../../../../hooks/useIntl';
import { isCartAdditionOption } from '../../../../../../utils/cart';
import { handleMissingCustomizationScroll, isRequiredHandled } from '../../../../../../utils/customizations';
import OptionGroupSection from '../../../../items/customization/OptionGroupSection';
import { useCheckout } from '../../../../../../context/CheckoutContext';
import { useCartStore } from '../../../../../../context/cart/CartStore';
import { OrderLineDto } from '../../../../../../../generated-api';

const getDefaultOptions = (orderLine: OrderLineDto, item: MenuItemVariantDto): CartAdditionOption[] =>
  orderLine.options
    .map((orderLineOption) => {
      const option = item.optionGroups
        .flatMap((group) => group.options)
        .find((itemOption) => itemOption.optionId === orderLineOption.itemVariantId);
      return option ? { option, quantity: orderLineOption.quantity, type: orderLineOption.type } : null;
    })
    .filter(isCartAdditionOption);

interface Props {
  onModalClose: VoidFunction;
  orderLine: OrderLineDto;
  item: MenuItemVariantDto;
}

const EditItemModal: FC<Props> = ({ orderLine, item, onModalClose }) => {
  const { isPhone } = useScreenSize();
  const { formatCurrency } = useLocation();
  const editCartItem = useCartStore((x) => x.editCartItem);
  const intl = useIntl();
  const [options, setOptions] = useState<CartAdditionOption[]>(getDefaultOptions(orderLine, item));
  const [quantity, setQuantity] = useState(orderLine.quantity);
  const { vatType } = useCheckout();

  const { hasAddedAllRequiredOptionGroups, requiredOptionGroups, addedRequiredOptionGroups } = useCustomisations(
    item,
    options
  );

  const handleAddToCart = () => {
    if (!hasAddedAllRequiredOptionGroups) {
      handleMissingCustomizationScroll(requiredOptionGroups, addedRequiredOptionGroups);
      return;
    }

    /**
     * OrderLine: the current item BEFORE editing
     * item: The new edited item
     * quantity: The new quantity for the whole item and orderLine.quantity is the old quantity
     */

    const orginalItemToDelete: DeleteItemsDto = {
      item: {
        courseId: orderLine.courseId,
        itemVariantId: orderLine.itemVariantId,
        name: orderLine.name,
        price: orderLine.price,
        takeawayPrice: orderLine.takeawayPrice,
        menuSectionId: orderLine.menuSectionId,
        sectionProductId: orderLine.sectionProductId,
        options: orderLine.options,
        comment: orderLine.comment,
      },
      quantity: orderLine.quantity,
    };

    const changedItemToAdd: AddItemsDto = {
      item: {
        courseId: orderLine.courseId,
        itemVariantId: item.variantLinkId,
        name: item.name,
        price: item.price,
        takeawayPrice: item.takeawayPrice,
        menuSectionId: item.menuSectionId,
        comment: orderLine.comment,
        sectionProductId: item.sectionProductId,
        options: options.map((option) => ({
          itemVariantId: option.option.optionId,
          name: option.option.name,
          price: option.option.price,
          takeawayPrice: option.option.takeawayPrice,
          quantity: option.quantity,
          type: option.type,
        })),
      },
      quantity,
    };

    const changeRequest: EditItemWrapper = {
      deleteItemsDto: orginalItemToDelete,
      addItemsDto: changedItemToAdd,
    };

    editCartItem(changeRequest);
    onModalClose();
  };

  const addItemLabel = `${intl.formatMessage({ id: 'items.addToCartButton' })} (${formatCurrency(
    sumCartAddition({ quantity, item, options, vatType })
  )})`;

  return (
    <QuantityDialog
      onClose={onModalClose}
      title={item.name}
      actionLabel={addItemLabel}
      onAction={handleAddToCart}
      isDisabledAction={!hasAddedAllRequiredOptionGroups}
      onQuantityChange={setQuantity}
      initialQuantity={quantity}
      size={isPhone ? 'fullwidth' : 'default'}
    >
      <article className="flex flex-col gap-6">
        <section>
          <p className="mb-3">{item.description}</p>
          <p className="text-grey-dark text-small">{item.allergens.map((a) => a.name).join(', ')}</p>
        </section>
        {item.optionGroups.map((optionGroup) => {
          const relevantDefaultOptions = options.filter((defaultOption) =>
            optionGroup.options.some((groupOption) => groupOption.optionId === defaultOption.option.optionId)
          );

          return (
            <OptionGroupSection
              key={optionGroup.variantLinkId}
              optionGroup={optionGroup}
              setOptions={setOptions}
              defaultOptions={relevantDefaultOptions}
              isRequiredHandled={isRequiredHandled(optionGroup, options)}
            />
          );
        })}
      </article>
    </QuantityDialog>
  );
};
export default EditItemModal;
